import styled from 'styled-components'

import { Typography } from 'components/Typography/Typography'
import { useGlobalBanner } from 'utils/useGlobalBanner'
import { GLOBAL_BANNER_HEIGHT } from 'components/GlobalBanner/GlobalBanner'

const Page = styled.div<{ $hasBanner: boolean }>`
  min-height: calc(100vh - 462px);
  margin-top: calc(124px + ${p => (p.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: calc(100px + ${p => (p.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);
  }
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 64px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0 16px 48px;
  }
`

const PageTitle = styled(Typography)`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Subtitle2}px;
  }
`

const Section = styled.div`
  margin-top: 40px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 28px;
  }
`

const Paragraph = styled(Typography)`
  margin-top: 16px;
  line-height: 1.7;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }
`

const BulletList = styled.ul`
  margin-top: 12px;
  padding-left: 24px;
`

const BulletItem = styled.li`
  margin-top: 8px;
  line-height: 1.7;
  color: ${p => p.theme.colors.text.primary};
  font-family: ${p => p.theme.typography.family.Poppins};
  font-size: ${p => p.theme.typography.size.Description}px;
  font-weight: ${p => p.theme.typography.weight.Regular};

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }
`

export const TermsOfUse = () => {
  const { isEnabled: hasBanner } = useGlobalBanner()

  return (
    <Page $hasBanner={hasBanner}>
      <Container>
        <PageTitle variant="title2" fontWeight="bold">
          Terms of Service
        </PageTitle>

        {/* 1. Acceptance of Terms */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            1. Acceptance of Terms
          </Typography>

          <Paragraph variant="description" fontWeight="medium">
            These Terms of Service (the &quot;Terms&quot;) govern your (&quot;you&quot; or the &quot;user&quot;) access
            to and use of the services made available by Kai Finance (&quot;Kai Finance&quot;, &quot;we&quot;,
            &quot;our&quot;, or &quot;us&quot;).
          </Paragraph>

          <Paragraph variant="description" fontWeight="medium">
            The &quot;Services&quot; include:
          </Paragraph>

          <BulletList>
            <BulletItem>the user interface located at https://kai.finance (the &quot;Interface&quot;); and</BulletItem>
            <BulletItem>
              any related features, tools, communications, support or other services that link to or reference these
              Terms.
            </BulletItem>
          </BulletList>

          <Paragraph variant="description">
            By using the Services, you confirm that you have read, understood, and agree to be bound by these Terms. If
            you access or use the Services on behalf of a legal entity, you represent and warrant that you have the
            authority to bind that entity to these Terms.
          </Paragraph>
        </Section>

        {/* 2. Eligibility and Compliance */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            2. Eligibility and Compliance
          </Typography>

          <Paragraph variant="description">
            Your use of the Services constitutes your representation and warranty that:
          </Paragraph>

          <BulletList>
            <BulletItem>
              you have full legal capacity, authority, and power to enter into and be bound by these Terms;
            </BulletItem>
            <BulletItem>
              you are not subject to any economic or trade sanctions, restrictive measures, or embargoes imposed by any
              governmental authority, and you are not listed on any sanctions or restricted persons list;
            </BulletItem>
            <BulletItem>
              your access to and use of the Services complies with all applicable laws in your jurisdiction, and you are
              solely responsible for ensuring such compliance.
            </BulletItem>
          </BulletList>
        </Section>

        {/* 3. Regulatory Status and Understanding the Protocol */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            3. Regulatory Status and Understanding the Protocol
          </Typography>

          <Paragraph variant="description" fontWeight="medium">
            Kai Finance is not a credit institution, investment company, brokerage, asset manager, payment institution,
            custodian, or other regulated financial intermediary. The Interface enables users to access decentralized,
            non-custodial smart contracts (the &quot;Protocol&quot;) deployed on public blockchain networks.
          </Paragraph>

          <Paragraph variant="description">
            The Protocol consists of autonomous, self-executing smart contracts that (i) operate isolated and
            permissionless lending markets; and (ii) provide vault mechanisms allocating user-supplied digital assets
            across supported on-chain opportunities. The smart contracts comprising the Protocol are deployed on the Sui
            Network, and interaction with the Protocol requires the use of a compatible Sui-supported non-custodial
            wallet.
          </Paragraph>

          <Paragraph variant="description">
            The Protocol operates solely according to its code and independently of Kai Finance. Kai Finance does not
            own or control the underlying blockchain networks, does not validate or reverse blockchain transactions, and
            does not control or modify smart contract execution.
          </Paragraph>

          <Paragraph variant="description">In particular, Kai Finance:</Paragraph>

          <BulletList>
            <BulletItem>does not create or maintain user accounts;</BulletItem>
            <BulletItem>does not custody digital assets, private keys, or recovery phrases;</BulletItem>
            <BulletItem>does not hold, access, or control users&apos; funds;</BulletItem>
            <BulletItem>does not initiate, execute, or settle transactions on behalf of users;</BulletItem>
            <BulletItem>
              does not act as broker, agent, custodian, portfolio manager, investment advisor, payment institution,
              credit institution, or any other regulated financial intermediary or service provider.
            </BulletItem>
          </BulletList>

          <Paragraph variant="description">
            Any yields, rewards, or returns are generated by on-chain mechanisms and are not guaranteed. Positions may
            be subject to liquidation in accordance with Protocol parameters, and deposits or withdrawals depend on
            available on-chain liquidity.
          </Paragraph>
        </Section>

        {/* 4. Wallet and Third-Party's Responsibility */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            4. Wallet and Third-Party&apos;s Responsibility
          </Typography>

          <Paragraph variant="description">
            Access to the Interface requires the use of a Sui-compatible wallet (e.g., Sui Wallet, MetaMask with Sui
            Snap, etc.) that enables interaction with public blockchain networks. All transactions are initiated and
            executed through your own wallet.
          </Paragraph>

          <Paragraph variant="description">
            Kai Finance does not create, access, control, custody, safeguard, or recover your wallet, private keys,
            recovery phrases, credentials, or digital assets. Kai Finance cannot access, retrieve, transfer, freeze,
            modify, or reverse blockchain transactions. You are solely responsible for maintaining the security and
            confidentiality of your wallet credentials and for all activities conducted through your wallet.
          </Paragraph>

          <Paragraph variant="description" fontWeight="medium">
            Your relationship with any wallet provider or other third-party service provider, including blockchain
            networks, infrastructure providers, or other integrated systems (&quot;Third-Party Services&quot;) is
            governed exclusively by the terms and policies of those third parties. Kai Finance does not own, control,
            operate, endorse, or guarantee any Third-Party Services and assumes no responsibility or liability for their
            availability, functionality, security, performance, or continued operation. Any interaction with Third-Party
            Services is undertaken at your own risk and discretion.
          </Paragraph>
        </Section>

        {/* 5. Technical Overview of Vaults and Protocol Mechanics */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            5. Technical Overview of Vaults and Protocol Mechanics
          </Typography>

          <Paragraph variant="description">
            The Services provide access to non-custodial smart-contract vaults deployed on the Sui Network. Vaults are
            autonomous software components that operate according to predefined on-chain logic and are designed to
            execute user-selected strategies without custody, control, or management of users&apos; digital assets by
            Kai Finance.
          </Paragraph>

          <Paragraph variant="description">
            The Protocol supports different vault configurations and operational mechanics, including Single-Asset
            Vaults and Liquidity Provider (&quot;LP&quot;) Vaults, leverage functionality, dynamic interest adjustments,
            automated compounding, and liquidation safeguards. Technical descriptions of vault structure, strategy
            execution, operational parameters, interest calculation, and incentive structures are available in the
            documentation section at https://docs.kai.finance.
          </Paragraph>

          <Paragraph variant="description">
            Users acknowledge that vault strategies are algorithmic in nature and may involve variable yield generation,
            borrowing activity, and exposure to market fluctuations. Participation in any vault involves financial and
            technical risks, and returns are not guaranteed.
          </Paragraph>

          <Paragraph variant="description">
            All vault operations are executed automatically through smart contracts. Blockchain transactions are final
            once confirmed and cannot be reversed.
          </Paragraph>
        </Section>

        {/* 6. Intellectual Property and Related Rights */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            6. Intellectual Property and Related Rights
          </Typography>

          <Paragraph variant="description">
            All intellectual property and related rights in the Services, including but not limited to the Interface,
            design, trademarks, documentation, and content provided by Kai Finance, remain the exclusive property of Kai
            Finance. Nothing in these Terms grants you any license or right to use such intellectual property except as
            necessary to access and use the Services in accordance with these Terms.
          </Paragraph>
        </Section>

        {/* 7. Interest, Fees and Taxes */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            7. Interest, Fees and Taxes
          </Typography>

          <Paragraph variant="description">
            You acknowledge and agree that you are solely responsible for the payment of any interest, fees, or other
            charges arising from or in connection with your interaction with the Protocol, as determined in accordance
            with the technical documentation available at https://docs.kai.finance.
          </Paragraph>

          <Paragraph variant="description">
            You are solely responsible for determining, calculating, reporting, and paying any taxes, duties, or
            governmental charges arising from your use of the Services. Kai Finance does not provide tax advice and
            assumes no responsibility for your tax obligations.
          </Paragraph>
        </Section>

        {/* 8. Assumption of Risk */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            8. Assumption of Risk
          </Typography>

          <Paragraph variant="description">
            By using the Services, you acknowledge and accept that blockchain technology, crypto-assets, and smart
            contracts involve significant, inherent, and unpredictable risks. You understand that such risks may result
            in partial or total loss of digital assets and assume full responsibility for all consequences arising from
            your use of the Services.
          </Paragraph>

          <Paragraph variant="description">Without limitation, you acknowledge that:</Paragraph>

          <BulletList>
            <BulletItem>
              smart contracts are autonomous, self-executing code and may contain bugs, vulnerabilities, design defects,
              or security flaws that could result in loss of funds or unintended outcomes;
            </BulletItem>
            <BulletItem>
              public blockchain networks may experience congestion, downtime, forks, validator failures, Protocol
              upgrades, or other technical disruptions;
            </BulletItem>
            <BulletItem>transactions recorded on a blockchain are irreversible;</BulletItem>
            <BulletItem>crypto-asset markets are highly volatile and subject to rapid price fluctuations;</BulletItem>
            <BulletItem>
              liquidity constraints, market conditions, or Protocol parameters may restrict or delay withdrawals or
              affect the value of positions;
            </BulletItem>
            <BulletItem>
              loss, compromise, or misuse of wallet credentials, private keys, or recovery phrases may result in
              permanent and unrecoverable loss of digital assets.
            </BulletItem>
          </BulletList>

          <Paragraph variant="description">
            The foregoing list is not exhaustive. You are solely responsible for evaluating the risks associated with
            the Services and for determining whether use of the Services is appropriate in light of your financial
            condition, technical knowledge, and risk tolerance.
          </Paragraph>
        </Section>

        {/* 9. Prohibited Conduct */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            9. Prohibited Conduct
          </Typography>

          <Paragraph variant="description">
            You agree (i) not to use the Services for any unlawful purpose or to facilitate illegal activity (including
            money laundering, terrorist financing, sanctions evasion, tax evasion); (ii) not to engage in unauthorised
            commercial exploitation of the Services; (iii) not to upload, transmit, or distribute malicious code; (iv)
            not to copy, modify, reverse engineer, decompile, disassemble, or otherwise attempt to extract source code
            from the Services; (v) not to interfere with or disrupt the operation, integrity, or security of the
            Services or other users&apos; access; (vi) not to circumvent or bypass any security measures; (vii) not to
            engage in any attack, exploit, manipulation, or denial-of-service activity targeting the Interface or the
            Protocol; (viii) not to infringe or violate any copyright, trademark, or other intellectual property or
            proprietary rights; (ix) not to engage in market manipulation or other abusive trading practices; (x) not to
            perform data mining, scraping, or automated extraction of data without authorisation; and (xi) not to engage
            in any other conduct that violates applicable laws.
          </Paragraph>
        </Section>

        {/* 10. Disclaimers */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            10. Disclaimers
          </Typography>

          <Paragraph variant="description" fontWeight="medium">
            Kai Finance and its affiliates, directors, key personnel, employees, and contractors (collectively, the
            &quot;Released Parties&quot;) make no representations or warranties, whether express, implied, or statutory,
            regarding the availability, continuity, reliability, accuracy, completeness, security, or suitability of the
            Services, the Protocol, or any Third-Party Services.
          </Paragraph>

          <Paragraph variant="description">
            Access to the Interface, the Protocol, or any integrated or referenced Third-Party Services may be
            interrupted, delayed, or unavailable. The Services may contain errors, defects, delays, or other technical
            issues. No statement, communication, or information provided by the Released Parties shall be interpreted as
            creating any warranty, representation, or guarantee.
          </Paragraph>

          <Paragraph variant="description">
            The Released Parties do not endorse, verify, control, or assume responsibility for any Third-Party Services,
            the operation or execution of smart contracts within the Protocol, or any advertisements, offers, or
            statements made by third parties, and assume no responsibility for their availability, security,
            performance, or compliance.
          </Paragraph>
        </Section>

        {/* 11. Limitation of Liability */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            11. Limitation of Liability
          </Typography>

          <Paragraph variant="description">
            To the fullest extent permitted under applicable law, the Released Parties shall not be liable for any
            direct or indirect damages, including loss of profits, revenues, digital assets, data, or goodwill, arising
            out of or in connection with:
          </Paragraph>

          <BulletList>
            <BulletItem>access to, or use of, or inability to access or use the Services;</BulletItem>
            <BulletItem>
              user errors, including incorrect transactions, lost private keys, or compromised wallet credentials;
            </BulletItem>
            <BulletItem>
              breach of the users&apos; representations, warranties, obligations, or any other provision under these
              Terms;
            </BulletItem>
            <BulletItem>violation of the prohibited conduct provision provided hereunder;</BulletItem>
            <BulletItem>
              unauthorised access, security breaches, or other malicious or unlawful interference with the Services,
              Interface, or Protocol;
            </BulletItem>
            <BulletItem>
              blockchain or network events, including forks, Protocol upgrades, validator failures, cybersecurity
              incidents, or other technical disruptions;
            </BulletItem>
            <BulletItem>
              use of or reliance on Third-Party Services, websites, applications, protocols, or resources accessible
              through the Services, including their security, availability, or performance;
            </BulletItem>
            <BulletItem>market volatility or fluctuations in the value of crypto-assets;</BulletItem>
            <BulletItem>acts, omissions, or misconduct of third parties; or</BulletItem>
            <BulletItem>changes in applicable laws or regulatory requirements.</BulletItem>
          </BulletList>

          <Paragraph variant="description">
            The Released Parties assume no liability for losses, damages, or injuries arising from (i) inaccuracies,
            omissions, or errors in information displayed through the Services; (ii) unauthorised or improper use of the
            Services; (iii) interruptions, suspension, or termination of functionality of the Services or any integrated
            Third-Party Services; (iv) transmission of malware or harmful code; (v) reliance on data or content obtained
            through the Services or third-party systems; and/or (vi) unlawful or abusive conduct by user or any third
            party.
          </Paragraph>
        </Section>

        {/* 12. Indemnification */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            12. Indemnification
          </Typography>

          <Paragraph variant="description">
            You agree to indemnify, defend, and hold harmless Kai Finance and other Released Parties from and against
            any and all claims, demands, actions, proceedings, damages, losses, liabilities, fines, penalties, costs,
            and expenses (including reasonable attorneys&apos; fees) arising out of or relating to (i) your access to or
            use of the Services, the Interface, the Protocol, or any Third-Party Services; (ii) your breach of these
            Terms and/or violation of any applicable law or sanctions regime; (iii) your infringement or alleged
            infringement of any third-party rights, including intellectual property, property, contractual,
            confidentiality, or privacy rights; (iv) any misuse of the Services or interaction with smart contracts
            through your wallet or credentials; (v) any digital assets, data, or content submitted or transmitted by you
            or with your assistance; (vi) any misrepresentation made by you; and/or (vii) any dispute between you and
            another user, third party, or your own customers or users.
          </Paragraph>

          <Paragraph variant="description">
            You agree to cooperate fully with the defence of any such claim and shall not settle or compromise any claim
            involving the Released Parties without Kai Finance&apos;s prior written consent. This indemnification
            obligation shall survive termination or cessation of your use of the Services.
          </Paragraph>
        </Section>

        {/* 13. Governing Law */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            13. Governing Law
          </Typography>

          <Paragraph variant="description">
            These Terms and any dispute or claim arising out of or in connection with them, or with your access to or
            use of the Services, shall be governed by and interpreted in accordance with the laws of the Republic of
            Croatia, without regard to its conflict of laws rules.
          </Paragraph>
        </Section>

        {/* 14. Dispute Resolution */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            14. Dispute Resolution
          </Typography>

          <Paragraph variant="description">
            We encourage the resolution of any disagreement through open and constructive communication. If a dispute
            arises, you agree to first contact us at kai@kunalabs.io and engage in good faith efforts to resolve the
            matter amicably before initiating any formal legal proceedings.
          </Paragraph>

          <Paragraph variant="description">
            If the dispute cannot be resolved within a reasonable period of time, any claim, controversy, or dispute
            arising out of or in connection with the Services, these Terms, or any related acts or omissions shall be
            subject to the exclusive jurisdiction of the competent courts in Zagreb, Republic of Croatia.
          </Paragraph>
        </Section>

        {/* 15. Amendments */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            15. Amendments
          </Typography>

          <Paragraph variant="description">
            We may amend or update these Terms from time to time to reflect changes in the Services, applicable law, or
            operational practices. When we make material changes, we may provide additional notice through the Interface
            or by other reasonable means where appropriate.
          </Paragraph>

          <Paragraph variant="description">
            Any amended Terms will become effective upon publication at https://kai.finance, unless otherwise stated.
            Your continued access to or use of the Services after such publication constitutes your acceptance of the
            revised Terms.
          </Paragraph>
        </Section>
      </Container>
    </Page>
  )
}
